<template>
    <UiModal
        :isOpen="show"
        @close="handleClose"
        width="720px"
        :fullScreenMobile="true"
        :title="cardNotListed && !goBack ? 'Missing Card Request' : ''"
        :icon="cardNotListed && !goBack ? 'unmatched-card' : ''"
    >
        <!-- Custom Title Section with Card Info -->
        <template
            #title
            v-if="!cardNotListed || (goBack && !showSuccessMessage)"
        >
            <div class="flex flex-col items-center gap-4 w-full pt-8">
                <!-- Card Icon Placeholder -->
                <div
                    class="w-36 h-24 flex items-center justify-center overflow-hidden"
                >
                    <!-- Show selected card image if available -->
                    <template
                        v-if="
                            selectedMatchCard &&
                            selectedMatchCard.card_image.path
                        "
                    >
                        <ImageLoader>
                            <template #image>
                                <img
                                    :src="`${cdnUrl}/${selectedMatchCard?.card_image.path}/_small/${selectedMatchCard?.card_image.file_name}`"
                                    class="w-full h-auto object-contain"
                                    alt="Selected card image"
                                />
                            </template>
                            <template #preloader>
                                <div
                                    class="w-36 h-24 bg-slate-200 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 shimmer"
                                ></div>
                            </template>
                            <template #error>
                                <div
                                    class="w-36 h-24 bg-slate-100 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center"
                                >
                                    <img
                                        src="/images/blank_card.png"
                                        class="w-full h-auto object-contain"
                                    />
                                </div>
                            </template>
                        </ImageLoader>
                    </template>
                    <!-- Default unknown card icon -->
                    <template v-else>
                        <div
                            class="w-36 h-24 bg-slate-100 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center"
                        >
                            <WalletIcon
                                icon="unknown-card"
                                class="w-6 h-6 text-slate-500 dark:text-slate-300"
                            />
                        </div>
                    </template>
                </div>

                <!-- Card Title -->
                <div class="w-full max-w-[560px] text-center">
                    <div
                        class="text-slate-900 dark:text-slate-100 text-2xl font-medium leading-loose"
                    >
                        <h1 v-if="selectedMatchCard">
                            {{ selectedMatchCard.title }}
                        </h1>
                        <h1
                            v-else-if="
                                selectedCard && selectedCard.card_match_request
                            "
                        >
                            {{
                                selectedCard.card_match_request
                                    .card_not_found_name
                            }}
                        </h1>
                        <h1 v-else>Unknown Card</h1>
                    </div>
                    <span
                        v-if="selectedCard && selectedCard.last_four"
                        class="text-slate-500 dark:text-slate-400 text-lg leading-7 ml-1"
                    >
                        {{ selectedCard.last_four }}
                    </span>
                </div>
            </div>
        </template>

        <!-- Main Content -->
        <template #content>
            <!-- Success Message Section -->
            <div
                v-if="showSuccessMessage"
                class="flex flex-col items-center w-full"
            >
                <!-- Success Icon Container -->
                <div
                    class="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center"
                >
                    <Icon
                        icon="check"
                        class="text-emerald-600 dark:text-emerald-300"
                        width="40"
                        height="40"
                    />
                </div>

                <!-- Success Message Content -->
                <div
                    class="w-full max-w-[560px] pt-4 flex flex-col items-center gap-4"
                >
                    <h3
                        class="text-xl font-medium text-slate-900 dark:text-slate-100 text-center w-full"
                    >
                        {{ successMessage?.header }}
                    </h3>
                    <p class="text-sm text-slate-700 dark:text-slate-300 text-center max-w-96">
                        {{ successMessage?.body }}
                    </p>
                </div>
            </div>
            <div v-else class="flex flex-col items-center px-4 sm:px-6 gap-2">
                <template v-if="isNewRequest || goBack">
                    <!-- bank connection -->
                    <p
                        v-if="
                            selectedCard &&
                            selectedCard.institution_code &&
                            selectedCard.institution_code !== 'zzz'
                        "
                        class="text-sm text-slate-900 dark:text-slate-100"
                    >
                        We couldn't recognize this card through the
                        <span class="font-semibold">
                            {{ selectedCard.issuingBank?.name }}
                        </span>
                        bank connection. Please select the correct card from the
                        list below or add the details for a card that's not
                        listed.
                    </p>
                    <!-- manually added -->
                    <p v-else>
                        We couldn't recognize this card through the bank
                        connection. Please select the correct card from the list
                        below or add the details for a card that's not listed.
                    </p>
                    <!-- Card Search - Hidden when using transition section below -->
                    <div v-if="false" class="w-full">
                        <label class="sr-only">Select card</label>
                        <!-- Card Search Component -->
                        <div class="w-full mx-auto mt-4">
                            <UserCardSearch
                                @cardSelected="cardSelected"
                                @clearCurrentCard="clearSelectedMatchCard"
                                placeholder="Search for a card"
                            />
                        </div>
                    </div>
                    <!-- Card Not Listed - Hidden when using transition section below -->
                    <transition name="fade">
                        <button
                            v-if="false"
                            @click="handleCardNotListed"
                            class="text-slate-900 dark:text-slate-100 text-sm font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-slate-400/50 self-start flex items-center gap-1"
                        >
                            <span>Card not listed?</span>
                        </button>
                    </transition>
                    <MissingCardRequestForm
                        v-if="cardNotListed && !requestAlreadyExists && !goBack"
                        v-model="missingCardDetails"
                        :errors="errors"
                        @clear-error="clearError"
                    />
                </template>
                <!-- Awaiting response -->
                <div v-else-if="isAwaitingResponse" class="flex flex-col gap-4">
                    <p>
                        Request submitted
                        {{
                            selectedCard && selectedCard.card_match_request
                                ? new Date(
                                      selectedCard.card_match_request.created_at
                                  ).toLocaleDateString('en-US', {
                                      month: 'numeric',
                                      day: 'numeric',
                                      year: '2-digit',
                                  })
                                : ''
                        }}
                    </p>
                    <MissingCardRequestForm
                        :disabled="isAwaitingResponse"
                        v-model="missingCardDetails"
                        :errors="errors"
                        @clear-error="clearError"
                    />
                    <p v-if="isAwaitingResponse" class="text-center">
                        We are processing your request to add this card to our
                        system. Please check back soon!
                    </p>
                </div>
                <!-- Card Selection Section -->
                <div class="w-full max-w-[672px] flex flex-col gap-2">
                    <!-- Card Selection Area - Either Search or Custom Form -->
                    <Transition name="fade" mode="out-in">
                        <!-- Card Search Option -->
                        <div
                            v-if="!cardNotListed || goBack"
                            class="w-full flex flex-col gap-3"
                        >
                            <div class="w-full">
                                <label class="sr-only">Select card</label>
                                <!-- Card Search Component -->
                                <div class="w-full mx-auto">
                                        <UserCardSearch
                                            @cardSelected="cardSelected"
                                            @clearCurrentCard="
                                                clearSelectedMatchCard
                                            "
                                            placeholder="Search for a card"
                                            :currentCard="selectedMatchCard"
                                        />
                                </div>
                            </div>

                            <!-- Card Not Listed Link -->
                            <transition name="fade">
                                <button
                                    v-if="
                                        !selectedMatchCard &&
                                        (!cardNotListed || goBack)
                                    "
                                    @click="handleCardNotListed"
                                    class="text-slate-900 dark:text-slate-100 text-sm font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-slate-400/50 self-start flex items-center gap-1"
                                >
                                    <span>Card not listed?</span>
                                </button>
                            </transition>
                        </div>

                        <!-- Make a new card match request -->
                        <div v-else class="w-full flex flex-col gap-3">
                            <!-- Card Not Listed Form Content -->
                            <!-- Form Title -->
                            <div>
                                <p
                                    class="text-sm text-slate-600 dark:text-slate-300"
                                    v-if="!selectedCard.card_match_request"
                                >
                                    Please provide details about
                                    <strong>
                                        Unknown card
                                        {{
                                            selectedCard.last_four
                                                ? `(${selectedCard.last_four})`
                                                : ''
                                        }}
                                    </strong>
                                    so we can add the correct card to our
                                    system. We will notify you once it's
                                    available.
                                </p>

                                <!-- Rejected Request -->
                                <div v-if="requestRejected">
                                    <!-- Rejection Notice -->
                                    <div
                                        v-if="
                                            selectedCard.card_match_request &&
                                            selectedCard.card_match_request
                                                .rejected &&
                                            selectedCard.card_match_request
                                                .rejected_reason
                                        "
                                        class="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/60 rounded-md"
                                    >
                                        <p
                                            class="text-sm text-red-700 dark:text-red-300 font-medium"
                                        >
                                            This missing card request submitted
                                            on
                                            <span>
                                                {{
                                                    selectedCard.card_match_request
                                                        ? new Date(
                                                              selectedCard.card_match_request.created_at
                                                          ).toLocaleDateString(
                                                              'en-US',
                                                              {
                                                                  month: 'numeric',
                                                                  day: 'numeric',
                                                                  year: '2-digit',
                                                              }
                                                          )
                                                        : ''
                                                }}
                                            </span>
                                            was rejected for the following
                                            reason:
                                            <br />
                                            <br />
                                            "{{
                                                selectedCard.card_match_request
                                                    .rejected_reason
                                            }}"
                                        </p>
                                    </div>

                                    <!-- Update Match Request -->
                                    <div class="mb-4 text-left">
                                        <p class="text-sm text-slate-600 dark:text-slate-300 my-2">
                                            Please update your request, being as
                                            specific as possible:
                                        </p>
                                        <MissingCardRequestForm
                                            v-model="missingCardDetails"
                                            :errors="errors"
                                            @clear-error="clearError"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </template>

        <!-- Footer with Action Buttons -->
        <template #footer v-if="!showSuccessMessage">
            <div class="flex justify-between gap-2 px-6 py-4">
                <span>
                    <TransitionGroup name="fade" tag="span" class="flex gap-2">
                        <!-- Back to Search Link -->
                        <button
                            v-show="cardNotListed && !goBack"
                            @click="goBack = true"
                            class="btn-base btn-white h-10"
                            key="back-to-search"
                        >
                            <span>
                                Back
                                <span class="sm:inline hidden">
                                    to card search
                                </span>
                            </span>
                        </button>
                    </TransitionGroup>
                </span>
                <span class="flex gap-2" v-if="!isAwaitingResponse || goBack">
                    <!-- Cancel Button -->
                    <button
                        @click="handleClose"
                        class="btn-base btn-white h-10"
                    >
                        Cancel
                    </button>

                    <!-- Confirm Button -->
                    <button
                        v-if="!requestRejected"
                        @click="submit"
                        :disabled="isMissingDetails"
                        class="btn-base btn-dark h-10 relative"
                        :class="{
                            'bg-slate-900 text-white hover:bg-slate-800':
                                !isSubmitting &&
                                (selectedMatchCard ||
                                    (cardNotListed && !isMissingDetails)),
                            'bg-slate-400 text-white cursor-not-allowed':
                                isSubmitting ||
                                (!selectedMatchCard &&
                                    (!cardNotListed || isMissingDetails)),
                        }"
                    >
                        <span class="flex items-center gap-2">
                            <span
                                v-if="isSubmitting"
                                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                            ></span>
                            {{
                                isSubmitting
                                    ? 'Processing...'
                                    : 'Confirm & check details'
                            }}
                        </span>
                    </button>
                    <button
                        v-else
                        @click="submit"
                        :disabled="isSubmitting || isMissingDetails"
                        class="btn-base btn-dark h-10 relative"
                        :class="{
                            'bg-slate-900 text-white hover:bg-slate-800':
                                !isSubmitting &&
                                (selectedMatchCard ||
                                    (cardNotListed && !isMissingDetails)),
                            'bg-slate-400 text-white cursor-not-allowed':
                                isSubmitting ||
                                (!selectedMatchCard &&
                                    (!cardNotListed || isMissingDetails)),
                        }"
                    >
                        <span class="flex items-center gap-2">
                            <span
                                v-if="isSubmitting"
                                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                            ></span>
                            {{
                                isSubmitting ? 'Processing...' : 'Save changes'
                            }}
                        </span>
                    </button>
                </span>
                <span v-if="isAwaitingResponse && !goBack">
                    <button @click="handleClose" class="btn-base btn-dark h-10">
                        Okay
                    </button>
                </span>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import WalletIcon from '@/components/wallet/wallet-icon.vue';
import UserCardSearch from '@/components/ui/user-card-search.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import MissingCardRequestForm from '@/components/wallet/modals/missing-card-request-form.vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet';

const cdnUrl = computed(() => window.cdnUrl);

// Props
const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

//Emits
const emit = defineEmits(['close', 'confirm', 'card-matched']);

// Store
const walletStore = useWalletStore();
const { showNotification } = walletStore;
const { selectedCard, wallet, cardMatchSkipToRequestForm } = storeToRefs(walletStore);

// Component state
const goBack = ref(false);
const isSubmitting = ref(false);
const successMessage = ref({
    header: 'Card request sent',
    body: 'Our team will review your request as soon as possible. You’ll get an email notification once the card is added to the list.',
});

// State
const selectedMatchCard = ref(null);
const cardNotListed = ref(false);
const missingCardDetails = ref({ issuing_bank: '', card_not_found_name: '' });
const errors = ref({});
const showSuccessMessage = ref(false);

const isMissingDetails = computed(() => {
    // If user selected a card from search, they have all required details
    if (selectedMatchCard.value) {
        return false;
    }
    // If showing the missing card form, check if required fields are filled
    if (cardNotListed.value && !goBack.value) {
        return (
            !missingCardDetails.value.issuing_bank.trim() ||
            !missingCardDetails.value.card_not_found_name.trim()
        );
    }
    // If showing card search but no card selected, details are missing
    return !selectedMatchCard.value;
});
const isAwaitingResponse = computed(() => {
    return (
        selectedCard.value &&
        selectedCard.value.card_match_request &&
        !selectedCard.value.card_match_request.resolved &&
        !selectedCard.value.card_match_request.rejected
    );
});

const requestRejected = computed(() => {
    return (
        selectedCard.value &&
        selectedCard.value.card_match_request &&
        selectedCard.value.card_match_request.rejected
    );
});

const isNewRequest = computed(() => {
    return (
        selectedCard.value &&
        !selectedCard.value.card_match_request &&
        cardNotListed.value
    );
});

const requestAlreadyExists = computed(() => {
    return selectedCard.value && selectedCard.value.card_match_request;
});

// Methods
const handleClose = () => {
    goBack.value = false;
    cardNotListed.value = false;
    selectedMatchCard.value = null;
    missingCardDetails.value = { issuing_bank: '', card_not_found_name: '' };
    errors.value = {};
    showSuccessMessage.value = false;
    emit('close');
};

const cardSelected = (card) => {
    selectedMatchCard.value = card;
};

const clearSelectedMatchCard = () => {
    selectedMatchCard.value = null;
};

const handleCardNotListed = () => {
    if (selectedCard.value && selectedCard.value.card_match_request) {
        goBack.value = false;
    } else {
        cardNotListed.value = true;
        goBack.value = false; // Reset goBack when showing the form
    }
};

const clearError = (field) => {
    if (errors.value[field]) {
        delete errors.value[field];
    }
};

const validateCustomCard = () => {
    const newErrors = {};

    if (
        !missingCardDetails.value.issuing_bank ||
        missingCardDetails.value.issuing_bank.trim() === ''
    ) {
        newErrors.issuing_bank = 'Please enter the issuing bank';
    }

    if (
        !missingCardDetails.value.card_not_found_name ||
        missingCardDetails.value.card_not_found_name.trim() === ''
    ) {
        newErrors.card_not_found_name = 'Please enter the credit card name';
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const submit = () => {
    isSubmitting.value = true;
    let data = {};
    let endpoint = '';

    // Check if wallet and selectedCard are available before accessing their properties
    if (!wallet.value || !wallet.value.id) {
        console.error('Wallet is not available');
        showNotification(
            'Error',
            'Wallet information is not available. Please try again.',
            'error'
        );
        isSubmitting.value = false;
        return;
    }

    if (!selectedCard.value || !selectedCard.value.id) {
        console.error('Selected card is not available');
        showNotification(
            'Error',
            'Card information is not available. Please try again.',
            'error'
        );
        isSubmitting.value = false;
        return;
    }

    if (selectedMatchCard.value) {
        // CASE 1: Make a card match:
        endpoint = `/wallets/${wallet.value.id}/cards/${selectedCard.value.id}/match`;
    } else if (!requestAlreadyExists.value) {
        // CASE2: first-time missing card report
        endpoint = `/wallets/${wallet.value.id}/cards/${selectedCard.value.id}`;
    } else {
        // CASE 3: Updating existing missing card request
        endpoint = `/wallets/${wallet.value.id}/cards/${selectedCard.value.id}/match-request`;
    }

    //Case 1:
    if (selectedMatchCard.value) {
        // Check if selectedMatchCard has an id before using it
        if (!selectedMatchCard.value.id) {
            console.error('Selected match card ID is not available');
            showNotification(
                'Error',
                'Card match information is incomplete. Please try again.',
                'error'
            );
            isSubmitting.value = false;
            return;
        }
        data = { matched_card_id: selectedMatchCard.value.id };
        ApiService.apiCall(endpoint, 'put', data)
            .then((response) => {
                if (response.status !== 200) {
                    const error = new Error('Validation error');
                    error.message = response.message;
                    throw error;
                }
                successMessage.value = {
                    header: 'Card matched successfully',
                    body: 'Your card has been matched successfully.',
                };
                showSuccessMessage.value = true;
                setTimeout(() => {
                    handleClose();
                    emit('card-matched', response.data.connected_account);
                }, 3000);
            })
            .catch((error) => {
                showNotification(
                    'Error',
                    error.message ||
                        'There was an error processing your request.',
                    'error'
                );
            })
            .finally(() => {
                isSubmitting.value = false;
            });
    } else {
        selectedCard.value.card_not_found = true;
        data = {
            card_not_found: true,
            card: selectedCard.value,
            card_not_found_name: missingCardDetails.value.card_not_found_name,
            issuing_bank: missingCardDetails.value.issuing_bank,
        };
        if (validateCustomCard()) {
            ApiService.apiCall(endpoint, 'put', data)
                .then((response) => {
                    if (response.status !== 200) {
                        const error = new Error('Validation error');
                        error.message = response.message;
                        throw error;
                    }

                    successMessage.value = {
                        header: `${selectedCard.value.card_match_request ? 'Card request updated' : 'Card request sent'}`,
                        body: `${selectedCard.value.card_match_request ? 'Your card request has been updated.' : 'Our team will review your request as soon as possible.'} You'll get an email notification once the card is added to the list.`,
                    };
                    showSuccessMessage.value = true;
                    emit('confirm');
                    setTimeout(() => {
                        handleClose();
                    }, 3000);
                })
                .catch((error) => {
                    // Handle Laravel validation errors
                    if (
                        error.response &&
                        error.response.status === 422 &&
                        error.response.data &&
                        error.response.data.errors
                    ) {
                        // Map Laravel validation errors to component error format
                        const validationErrors = error.response.data.errors;
                        errors.value = {};

                        // Map card.card_not_found_bank to issuing_bank
                        if (validationErrors['card.card_not_found_bank']) {
                            errors.value.issuing_bank =
                                validationErrors['card.card_not_found_bank'][0];
                        }

                        // Map card.card_not_found_name to card_not_found_name
                        if (validationErrors['card.card_not_found_name']) {
                            errors.value.card_not_found_name =
                                validationErrors['card.card_not_found_name'][0];
                        }
                    } else {
                        // Show generic error notification for non-validation errors
                        showNotification(
                            'Error',
                            error.message ||
                                'There was an error processing your request.',
                            'error'
                        );
                    }
                })
                .finally(() => {
                    isSubmitting.value = false;
                });
        } else {
            isSubmitting.value = false;
        }
    }
};

watch(
    () => selectedCard.value,
    (newCard) => {
        if (newCard?.card_match_request) {
            cardNotListed.value = true;
            missingCardDetails.value = {
                issuing_bank: newCard.card_match_request.issuing_bank || '',
                card_not_found_name:
                    newCard.card_match_request.card_not_found_name || '',
            };
        } else {
            cardNotListed.value = false;
            missingCardDetails.value = {
                issuing_bank: '',
                card_not_found_name: '',
            };
        }
    },
    { immediate: true }
);

// Notification deep-links (NoCardMatch / RejectedResolvedAccount) open this
// modal already on step 2. The store flag flips us straight to the request
// form regardless of whether selectedCard already has a card_match_request.
watch(
    () => [props.show, cardMatchSkipToRequestForm.value],
    ([isShown, skip]) => {
        if (isShown && skip) {
            cardNotListed.value = true;
            goBack.value = false;
        }
    },
    { immediate: true }
);
</script>

<style scoped>
/* Transition styles */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
